//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import { useEffect, useState } from 'react';
import { SelectValue } from 'antd/lib/select';

import { PicklistValue } from 'components/inputs/types';

import { FuzzyOptionState } from '../types';
import { parseFuzzyValue } from '../utils/fuzzyValueHelpers';

/**
 * Custom hook to manage fuzzy select state
 * Handles selected value, expanded option, and search input
 */
export const useFuzzyState = (
  value?: SelectValue,
  defaultValue?: SelectValue,
  values?: PicklistValue[],
  optionData: PicklistValue[] = [],
  onChange?: (value: SelectValue) => void
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<SelectValue | undefined>(defaultValue || value);
  const [expandedFuzzyOption, setExpandedFuzzyOption] = useState<FuzzyOptionState | null>(null);

  // Sync external value changes with internal state
  useEffect(() => {
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value);

      // If it's a fuzzy value, parse and set expanded state
      if (typeof value === 'string' && value) {
        const { value: baseValue, percentage, typoCount } = parseFuzzyValue(value);
        if (percentage !== null || typoCount !== null) {
          const optionsToUse = values || optionData;
          const option = optionsToUse?.find((opt) => opt.value === baseValue);
          if (option) {
            setExpandedFuzzyOption({ option, percentage, typoCount });
          }
        }
      }
    }
  }, [value, selectedValue, values, optionData]);

  // Reset internal state when values prop changes (e.g., when left field changes)
  useEffect(() => {
    const optionsToUse = values || optionData;

    if (optionsToUse && optionsToUse.length > 0) {
      // Check if current selected value exists in new options list
      if (selectedValue) {
        const valStr = typeof selectedValue === 'string' ? selectedValue : String(selectedValue);
        const { value: baseValue } = parseFuzzyValue(valStr);
        const valueExistsInNewOptions = optionsToUse.some((opt) => opt.value === baseValue);

        // If current value doesn't exist in new options, reset to empty or defaultValue
        if (!valueExistsInNewOptions) {
          const valueToSet = '';
          setSelectedValue(valueToSet);
          onChange?.(valueToSet);
          setExpandedFuzzyOption(null);
          setSearchValue('');
          setIsOpen(false);
        }
      } else if (!defaultValue && optionsToUse.length > 0) {
        setSelectedValue(undefined);
        onChange?.('');
        setSearchValue('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, optionData, selectedValue]);

  return {
    isOpen,
    setIsOpen,
    searchValue,
    setSearchValue,
    selectedValue,
    setSelectedValue,
    expandedFuzzyOption,
    setExpandedFuzzyOption,
  };
};
